/**
 * Variant Selects Web Component
 * Handles variant option selection with cascading availability
 */
if (!customElements.get('variant-selects')) {
  customElements.define(
    'variant-selects',
    class VariantSelects extends HTMLElement {
      constructor() {
        super();
        this.addEventListener('change', this.onOptionChange.bind(this));
        this.productVariants = this.getProductVariants();
      }

      connectedCallback() {
        // Initialize quantity max on page load for the initially selected variant
        requestAnimationFrame(() => {
          const selectedOptions = this.getSelectedOptions();
          const initialVariant = this.findMatchingVariant(selectedOptions);
          if (initialVariant) {
            this.updateQuantityMax(initialVariant);
          }
        });
      }

      getProductVariants() {
        const variantsJson = this.querySelector('[data-product-variants]');
        if (variantsJson) {
          try {
            return JSON.parse(variantsJson.textContent);
          } catch (e) {
            console.error('Error parsing variants JSON:', e);
            return [];
          }
        }
        return [];
      }

      onOptionChange(event) {
        const target = event.target;
        if (target.type !== 'radio') return;

        // Get all selected options
        let selectedOptions = this.getSelectedOptions();
        
        // Update availability of subsequent options (cascading)
        // This also auto-selects first available if current is unavailable
        selectedOptions = this.updateOptionAvailability(selectedOptions);
        
        // Find matching variant with updated selections
        const matchingVariant = this.findMatchingVariant(selectedOptions);
        
        // Update price and availability
        if (matchingVariant) {
          this.updatePrice(matchingVariant);
          this.updateAddToCartButton(matchingVariant);
          this.updateURL(matchingVariant);
          this.updateVariantInput(matchingVariant);
          this.updateQuantityMax(matchingVariant);
        } else {
          this.setUnavailable();
        }

        // Update selected value display
        this.updateSelectedValueDisplay(target);
        
        // Publish event for other components
        if (typeof publish !== 'undefined') {
          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              variant: matchingVariant,
              sectionId: this.dataset.section,
            },
          });
        }
      }

      getSelectedOptions() {
        const options = [];
        this.querySelectorAll('fieldset').forEach((fieldset) => {
          const checked = fieldset.querySelector('input[type="radio"]:checked');
          if (checked) {
            options.push(checked.value);
          }
        });
        return options;
      }

      findMatchingVariant(selectedOptions) {
        return this.productVariants.find((variant) => {
          return variant.options.every((option, index) => {
            return option === selectedOptions[index];
          });
        });
      }

      /**
       * Update availability of options based on currently selected values
       * Implements cascading availability (step selection)
       * Auto-selects first available option if current selection is unavailable
       * Returns updated selectedOptions array
       */
      updateOptionAvailability(selectedOptions) {
        const fieldsets = this.querySelectorAll('fieldset');
        const updatedOptions = [...selectedOptions];
        
        fieldsets.forEach((fieldset, optionIndex) => {
          // Skip first option - always check all variants
          if (optionIndex === 0) return;
          
          const inputs = fieldset.querySelectorAll('input[type="radio"]');
          let currentChecked = fieldset.querySelector('input[type="radio"]:checked');
          let firstAvailableInput = null;
          let isCurrentAvailable = false;
          
          inputs.forEach((input) => {
            const optionValue = input.value;
            
            // Check if this option value is available given previous selections
            const isAvailable = this.isOptionAvailable(
              optionIndex,
              optionValue,
              updatedOptions
            );
            
            const label = input.nextElementSibling;
            
            if (isAvailable) {
              input.disabled = false;
              label?.classList.remove('product-form__option-label--disabled');
              
              // Track first available option
              if (!firstAvailableInput) {
                firstAvailableInput = input;
              }
              
              // Check if current selection is available
              if (currentChecked && input.value === currentChecked.value) {
                isCurrentAvailable = true;
              }
            } else {
              input.disabled = true;
              label?.classList.add('product-form__option-label--disabled');
            }
          });
          
          // If current selection is unavailable, auto-select first available
          if (!isCurrentAvailable && firstAvailableInput) {
            // Uncheck current
            if (currentChecked) {
              currentChecked.checked = false;
            }
            
            // Check first available
            firstAvailableInput.checked = true;
            updatedOptions[optionIndex] = firstAvailableInput.value;
            
            // Update the selected value display
            const selectedValueDisplay = fieldset.querySelector('[data-selected-value]');
            if (selectedValueDisplay) {
              selectedValueDisplay.textContent = firstAvailableInput.value;
            }
          }
        });
        
        return updatedOptions;
      }

      /**
       * Check if an option value is available given previous selections
       */
      isOptionAvailable(optionIndex, optionValue, selectedOptions) {
        return this.productVariants.some((variant) => {
          // Check if variant has this option value
          if (variant.options[optionIndex] !== optionValue) {
            return false;
          }
          
          // Check if variant matches all PREVIOUS selections
          for (let i = 0; i < optionIndex; i++) {
            if (variant.options[i] !== selectedOptions[i]) {
              return false;
            }
          }
          
          // Check if variant is available (has stock)
          return variant.available;
        });
      }

      updatePrice(variant) {
        // Update both price containers (header and form area)
        const priceContainers = [
          document.getElementById(`price-${this.dataset.section}`),
          document.getElementById(`price-form-${this.dataset.section}`)
        ];
        
        priceContainers.forEach(priceContainer => {
          if (!priceContainer) return;

          const formattedPrice = this.formatMoney(variant.price);
          const priceItem = priceContainer.querySelector('.price-item--regular, .price-item--sale, .price-item');
          
          if (priceItem) {
            priceItem.textContent = formattedPrice;
          }

          // Handle compare at price (sale price)
          const priceDiv = priceContainer.querySelector('.price');
          if (priceDiv) {
            if (variant.compare_at_price && variant.compare_at_price > variant.price) {
              priceDiv.classList.add('price--on-sale');
            } else {
              priceDiv.classList.remove('price--on-sale');
            }
          }
        });
      }

      formatMoney(cents) {
        const currencySymbol = document.body.dataset.currency || '¥';
        const amount = (cents / 100).toLocaleString('ja-JP');
        return `${currencySymbol}${amount}`;
      }

      updateAddToCartButton(variant) {
        const productForm = document.querySelector(`product-form[data-section-id="${this.dataset.section}"]`) ||
                           document.querySelector('product-form');
        if (!productForm) return;

        const submitButton = productForm.querySelector('[type="submit"]');
        const submitButtonText = submitButton?.querySelector('span');

        if (!variant.available) {
          submitButton?.setAttribute('disabled', 'disabled');
          if (submitButtonText) {
            submitButtonText.textContent = window.variantStrings?.soldOut || '売り切れ';
          }
        } else {
          submitButton?.removeAttribute('disabled');
          if (submitButtonText) {
            submitButtonText.textContent = window.variantStrings?.addToCart || 'カートに入れる';
          }
        }
      }

      setUnavailable() {
        const productForm = document.querySelector(`product-form[data-section-id="${this.dataset.section}"]`) ||
                           document.querySelector('product-form');
        if (!productForm) return;

        const submitButton = productForm.querySelector('[type="submit"]');
        const submitButtonText = submitButton?.querySelector('span');

        submitButton?.setAttribute('disabled', 'disabled');
        if (submitButtonText) {
          submitButtonText.textContent = window.variantStrings?.unavailable || 'UNAVAILABLE';
        }
      }

      updateURL(variant) {
        if (!variant) return;
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }

      updateVariantInput(variant) {
        const variantInputs = document.querySelectorAll(`input[name="id"][form*="${this.dataset.section}"], .product-variant-id, input[name="id"]`);
        variantInputs.forEach((input) => {
          input.value = variant.id;
          if (variant.available) {
            input.removeAttribute('disabled');
          } else {
            input.setAttribute('disabled', 'disabled');
          }
        });
      }

      updateSelectedValueDisplay(target) {
        const fieldset = target.closest('fieldset');
        if (!fieldset) return;
        
        const selectedValueDisplay = fieldset.querySelector('[data-selected-value]');
        if (selectedValueDisplay) {
          selectedValueDisplay.textContent = target.value;
        }
      }

      updateQuantityMax(variant) {
        const quantityInput = document.querySelector('quantity-input input[name="quantity"]');
        if (!quantityInput) return;
        
        // Only set max if inventory is tracked and overselling is denied
        if (variant.inventory_management === 'shopify' && variant.inventory_policy === 'deny') {
          // Set max to inventory quantity (including 0)
          const maxQty = Math.max(0, variant.inventory_quantity);
          quantityInput.max = maxQty;
          
          // If current value exceeds new max, reduce it
          const currentValue = parseInt(quantityInput.value) || 1;
          if (maxQty > 0 && currentValue > maxQty) {
            quantityInput.value = maxQty;
            // Dispatch change event to update form
            quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
          } else if (maxQty === 0) {
            // If no stock, set value to 1 (min) but max will prevent increase
            quantityInput.value = 1;
          }
        } else {
          // Remove max limit if not tracking or allowing overselling
          quantityInput.removeAttribute('max');
        }
        
        // Trigger validation
        const quantityComponent = document.querySelector('quantity-input');
        if (quantityComponent && quantityComponent.validateQtyRules) {
          quantityComponent.validateQtyRules();
        }
      }
    }
  );
}

/**
 * Option Select Event Handler
 * Updates the selected value display when option select changes
 */
document.addEventListener('DOMContentLoaded', () => {
  const optionSelectInputs = document.querySelectorAll('input[data-option-select-option]');
  
  optionSelectInputs.forEach(input => {
    input.addEventListener('change', (event) => {
      const fieldset = event.target.closest('fieldset');
      if (!fieldset) return;
      
      const selectedDisplay = fieldset.querySelector('[data-selected-option-select]');
      if (selectedDisplay) {
        selectedDisplay.textContent = event.target.value;
      }
    });
  });
});
